import React, { Component } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import GridLayout, { noCompactor } from "react-grid-layout";
import { GoPlus, GoInfo } from "react-icons/go";
import { GrRevert } from "react-icons/gr";
import { TfiSave } from "react-icons/tfi";
import { BiExpandHorizontal, BiCollapseHorizontal } from "react-icons/bi";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import Tooltip from "../../../controls/common/tooltip";
import LibraryPane from "../../../controls/library/index";
import { removeComponentById } from "../../widgets/common/widgetBase";
import DetailsPane from "../../details/index";

import { findFreeGridCell } from "../../../controls/library/placement";
import GridConverter from "./converter";
import ChatNode from "../../widgets/chat/index";
import GraphNode from "../../widgets/graph/index";
import HtmlNode from "../../widgets/html/index";
import MessageNode from "../../widgets/message/index";
import MessagesNode from "../../widgets/messages/index";
import ThreadNode from "../../widgets/thread/index";
import TradingViewNode from "../../widgets/tradingview/index";
import MissionsNode from "../../widgets/missionControl/missions/index";
import "./grid.css";

const WIDGET_BY_TYPE = {
  "widget.chat": ChatNode,
  "widget.graph": GraphNode,
  "widget.html": HtmlNode,
  "widget.message": MessageNode,
  "widget.messages": MessagesNode,
  "widget.thread": ThreadNode,
  "widget.tradingview": TradingViewNode,
  "system.work.missions": MissionsNode,
};

const DEFAULT_DROPPING_ITEM = { i: "__dropping__", w: 4, h: 4 };

/**
 * The grid is a collapsed-border table: no gutter between cells, no inset against the canvas
 * walls, and every boundary is ONE pixel that two neighbouring cells share.
 *
 * The sharing is what the -1 margin buys. react-grid-layout snaps an item's width so that
 * `left + width === siblingLeft - margin`; at margin -1 that is `siblingLeft + 1`, so each cell
 * reaches one pixel INTO the next and the two cells' borders land on the very same pixel column.
 * Every cell can then carry a plain four-sided 1px border and no line ever doubles — which in
 * turn means selecting a widget is a pure colour change on a border it already owns, with no
 * geometry to compensate for and nothing inside the widget moving.
 *
 * It also closes the lattice without help. The first cell's left border is the left wall, the
 * last cell's right border is the right wall, so the canvas needs no border of its own and a
 * selected widget can paint the wall pixel like any other.
 *
 * The arithmetic that falls out (see columnStep / effectiveGridGeometry): n cells of `step`
 * pixels plus the one closing pixel of the last border fill the canvas exactly, so
 * `step = (canvas - 1) / n` and the cell box react-grid-layout is given is `step + 1`.
 *
 * These are constants, not reads of `layout.margin` / `layout.container_padding`. The backend
 * still default-fills both to [10, 10] on every stored definition and neither is editable
 * anywhere in the UI, so honoring them would only mean carrying the old gutter forward for
 * every playground that exists.
 */
const GRID_MARGIN = [-1, -1];
const GRID_CONTAINER_PADDING = [0, 0];

// A selected cell has to paint the boundary pixels it shares with its neighbours, so it must
// outrank them. Cells are otherwise stacked by their index in the layout; this sits above any
// of those and below the floating control panes (z-index 1000 in grid.css).
const SELECTED_CELL_Z = 500;

export default class GridWorkspace extends Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.state = {
      active: false,
      containerWidth: 1200,
      // Measured visible height of .grid-canvas; drives how many full rows
      // fit. Default matches the canvas CSS min-height for a sane first paint.
      containerHeight: 600,
      droppingItem: DEFAULT_DROPPING_ITEM,
      // True while a drag or resize gesture is in progress. Used to freeze
      // geometry in "fit" mode so the row height does not rescale mid-gesture.
      interacting: false,
    };
    // Cached geometry from the last render; used to freeze geometry during
    // an active drag/resize (interacting === true) in fit/fixed_rows modes.
    this.lastGeometry = null;
    this.converter = new GridConverter(props.shell.shellInterface);
  }

  componentDidMount() {
    this.measureContainer();
    window.addEventListener("resize", this.measureContainer);

    // Safety net: if react-grid-layout ever misses an onDragStop/onResizeStop
    // (e.g. the pointer is released outside the window), a window-level mouseup
    // still clears the interaction flag so "fit" geometry doesn't stay frozen.
    window.addEventListener("mouseup", this.handleGlobalPointerUp);

    // Library/details pane toggles change the canvas width without firing a
    // window resize. Observe the canvas itself so the grid re-measures on
    // any layout-driven size change.
    if (typeof ResizeObserver !== "undefined" && this.containerRef.current) {
      this.containerObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.containerObserver.observe(this.containerRef.current);
    }

    this.handleSidebarDragStart = (ev) => {
      const componentDef = ev?.detail?.componentDef;
      if (!componentDef) return;
      const layout = this.resolveLayout();
      if (!layout) return;
      const gridGeometry = {
        containerWidth: this.containerWidth(),
        columns: layout.columns,
        rowHeight: this.effectiveGridGeometry(layout).rowHeight,
        margin: GRID_MARGIN,
        containerPadding: GRID_CONTAINER_PADDING,
      };
      const { w, h } = this.converter.getComponentGridSize(
        componentDef, this.props.shell.manifest, gridGeometry,
      );
      this.setState({ droppingItem: { i: "__dropping__", w, h } });
    };
    document.addEventListener("playground-widget-drag-start", this.handleSidebarDragStart);

    // Escape clears the current selection; Delete/Backspace removes the selected widget.
    //
    // Delete is guarded so it can't repeat the failure that originally kept it unbound: a Delete
    // pressed inside a nested widget (e.g. editing a step in the Mission Control workflow editor)
    // used to take the whole widget with it. The key only removes the widget when focus is NOT inside
    // it — select a widget by clicking its frame and Delete removes it; click into its content first
    // and the key belongs to that content. Mirrors the free-form canvas handler.
    this.handleKeyDown = (event) => {
      // Skip when the user is typing — these shortcuts are widget-level, not text-input-level.
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;

      const shellInterface = this.props.shell?.shellInterface;
      if (!shellInterface) return;

      if (event.key === "Escape" && shellInterface.updateDetails) {
        shellInterface.updateDetails(null, null);
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        if (!shellInterface.canEditRevision?.()) return;
        const details = this.props.shell?.details;
        if (details?.view !== "component" || !details?.data?.id) return;
        if (this.isFocusInsideComponent(details.data.id)) return;
        event.preventDefault();
        removeComponentById(shellInterface, details.data.id);
      }
    };
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.measureContainer);
    window.removeEventListener("mouseup", this.handleGlobalPointerUp);
    document.removeEventListener("playground-widget-drag-start", this.handleSidebarDragStart);
    document.removeEventListener("keydown", this.handleKeyDown);
    if (this.containerObserver) {
      this.containerObserver.disconnect();
      this.containerObserver = null;
    }
    if (this.measureFrame) {
      window.cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
    }
  }

  /**
   * Measure on the NEXT frame, never inside the observer's own delivery.
   *
   * Measuring here sets state, which re-lays out the grid, which changes the height of the very
   * element being observed — a `fit`-sized playground closes that loop by design. The browser cannot
   * settle it within one frame, so it defers the remaining notifications and reports
   * "ResizeObserver loop completed with undelivered notifications". Nothing is broken when it does —
   * the deferred notifications arrive next frame — but it is raised as a window error, which is
   * enough for the dev server's overlay to throw a red screen over the app. Session sync is what
   * made it routine: every arriving item changes a widget's height.
   *
   * Coalescing is the other half: a burst of observations inside one frame measures once.
   */
  scheduleMeasure() {
    if (this.measureFrame) return;
    this.measureFrame = window.requestAnimationFrame(() => {
      this.measureFrame = null;
      this.measureContainer();
    });
  }

  measureContainer = () => {
    if (!this.containerRef.current) return;
    const nextWidth = this.containerRef.current.clientWidth;
    const nextHeight = this.containerRef.current.clientHeight;
    // Skip zero-size measurements (display: none on the playground tab
    // makes clientWidth/clientHeight 0). Without this, ResizeObserver fires
    // twice on every tab switch — 0 on hide, N on show — and the grid
    // reflows on return even though nothing visually changed.
    if (nextWidth <= 0 || nextHeight <= 0) return;
    if (nextWidth === this.state.containerWidth && nextHeight === this.state.containerHeight) return;
    this.setState({ containerWidth: nextWidth, containerHeight: nextHeight });
  };

  containerWidth() {
    // clientWidth of .grid-canvas, so any scrollbar is already excluded: this is the box the
    // cells tile. With zero container padding the first cell's left border is the pixel at
    // x = 0 and the last cell's right border is the pixel at x = width - 1.
    return Math.max(0, this.state.containerWidth || 1200);
  }

  /**
   * Returns the layout block from the loaded definition JSON. The DDB
   * metadata record only carries layout on the create response, so for
   * any reloaded playground we have to read from the JSON backup.
   */
  resolveLayout() {
    return this.props.shell?.definitionObjectBackup?.layout
      ?? this.props.shell?.definition?.layout
      ?? null;
  }

  canEdit() {
    return !!this.props.shell?.shellInterface?.canEditRevision?.();
  }

  handleLayoutChange = (nextLayoutItems) => {
    if (!this.canEdit()) return;
    const byId = new Map(nextLayoutItems.map((it) => [it.i, it]));

    const changed = this.props.shell.nodes.some((node) => {
      const next = byId.get(node.id);
      return next && (
        next.x !== node.layout.x || next.y !== node.layout.y ||
        next.w !== node.layout.w || next.h !== node.layout.h
      );
    });
    if (!changed) return;

    const updatedNodes = this.props.shell.nodes.map((node) => {
      const nextItem = byId.get(node.id);
      if (!nextItem) return node;
      return { ...node, layout: { ...node.layout, ...nextItem } };
    });

    this.props.shell.workflowInterface.setNodes(updatedNodes, /*isModified=*/true);
  };

  handleInteractionStart = () => this.setState({ active: true, interacting: true });

  // Clearing interacting triggers a re-render so geometry recomputes from
  // the committed layout (e.g. "fit" snaps to the new content bounds).
  handleInteractionEnd = () => this.setState({ active: false, interacting: false });

  // Only acts if an interaction is still flagged, so normal clicks are a no-op.
  handleGlobalPointerUp = () => {
    if (this.state.interacting) this.handleInteractionEnd();
  };

  handleDrop = (_layout, layoutItem, event) => {
    if (!this.canEdit()) return;
    // The library panel serializes the full component definition as JSON
    // under the "text/plain" key on dragstart.
    const raw = event.dataTransfer?.getData("text/plain");
    if (!raw) return;
    let componentDef;
    try {
      componentDef = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!componentDef || !componentDef.type) return;

    const newNode = this.converter.createNewNode(
      componentDef,
      { x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h },
      this.props.shell.manifest,
    );

    const nextNodes = [...this.props.shell.nodes, newNode];
    this.props.shell.workflowInterface.setNodes(nextNodes, /*isModified=*/true);
  };

  handleAddComponent = (componentDef) => {
    if (!this.canEdit()) return;
    const layout = this.resolveLayout();
    if (!layout) return;

    const gridGeometry = {
      containerWidth: this.containerWidth(),
      columns: layout.columns,
      rowHeight: this.effectiveGridGeometry(layout).rowHeight,
      margin: GRID_MARGIN,
      containerPadding: GRID_CONTAINER_PADDING,
    };
    const { w, h } = this.converter.getComponentGridSize(
      componentDef, this.props.shell.manifest, gridGeometry,
    );
    const width = Math.min(w, layout.columns);
    const layoutItems = this.props.shell.nodes.map((n) => n.layout);
    const { x, y } = findFreeGridCell(layoutItems, width, h, layout.columns);

    const newNode = this.converter.createNewNode(
      componentDef, { x, y, w: width, h }, this.props.shell.manifest,
    );
    const nextNodes = [...this.props.shell.nodes, newNode];
    this.props.shell.workflowInterface.setNodes(nextNodes, /*isModified=*/true);
  };

  // HTML5 drag-and-drop fallback on the canvas itself. react-grid-layout's
  // internal isDroppable handling can miss drops when the grid is empty
  // (its inner div collapses) or when the cursor is over canvas padding,
  // so we accept drops on the wrapper and compute the grid cell ourselves.
  handleCanvasDragOver = (event) => {
    if (!this.canEdit()) return;
    if (!event.dataTransfer) return;
    if (Array.from(event.dataTransfer.types || []).includes("text/plain")) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    }
  };

  handleCanvasDrop = (event) => {
    if (!this.canEdit()) return;
    const raw = event.dataTransfer?.getData("text/plain");
    if (!raw) return;
    event.preventDefault();
    let componentDef;
    try {
      componentDef = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!componentDef || !componentDef.type) return;

    const layout = this.resolveLayout();
    if (!layout) return;

    const canvas = this.containerRef.current;
    if (!canvas) return;
    // getBoundingClientRect() measures the border box, so clientLeft/clientTop (the frame
    // border widths) come off to land in the same content-box origin the cells tile from.
    const rect = canvas.getBoundingClientRect();
    const offsetX = event.clientX - rect.left - canvas.clientLeft + canvas.scrollLeft;
    const offsetY = event.clientY - rect.top - canvas.clientTop + canvas.scrollTop;

    // Boundaries sit at whole multiples of the step, so the cell under a point is just the
    // point divided by the step. The cell BOX is a pixel wider than that, but the extra pixel
    // is the shared border, which belongs to the next cell just as much as to this one.
    const columnStep = this.columnStep(layout);
    const { rowStep } = this.effectiveGridGeometry(layout);

    const xCells = columnStep > 0 ? Math.max(0, Math.floor(offsetX / columnStep)) : 0;
    const yCells = rowStep > 0 ? Math.max(0, Math.floor(offsetY / rowStep)) : 0;

    const dropping = this.state.droppingItem || DEFAULT_DROPPING_ITEM;
    const w = Math.min(dropping.w || 4, layout.columns);
    const h = dropping.h || 4;
    const x = Math.min(xCells, Math.max(0, layout.columns - w));
    const y = yCells;

    const newNode = this.converter.createNewNode(
      componentDef,
      { x, y, w, h },
      this.props.shell.manifest,
    );

    const nextNodes = [...this.props.shell.nodes, newNode];
    this.props.shell.workflowInterface.setNodes(nextNodes, /*isModified=*/true);
  };

  // Live shell value takes priority over the persisted layout block; falls back to "pitch".
  resolveVerticalSizing(layout) {
    return this.props.shell?.verticalSizing ?? layout?.vertical_sizing ?? "pitch";
  }

  // Live shell value takes priority over the persisted layout block; clamped to 1..128.
  resolveReferenceRows(layout) {
    const v = this.props.shell?.referenceRows ?? layout?.reference_rows ?? 12;
    return Math.max(1, Math.min(128, parseInt(v, 10) || 12));
  }

  // The 1-based row index of the bottom edge of the lowest widget (0 when grid is empty).
  contentMaxRow() {
    const nodes = this.props.shell?.nodes || [];
    let maxRow = 0;
    for (const n of nodes) {
      const y = (n.layout && n.layout.y) || 0;
      const h = (n.layout && n.layout.h) || 0;
      if (y + h > maxRow) maxRow = y + h;
    }
    return maxRow; // 0 when empty
  }

  // Derive the effective row count and row geometry for the current sizing mode.
  // "pitch"      — treat layout.row_height as a target pitch; derive row count so a whole
  //                number of full rows exactly fills the visible canvas.
  // "fit"        — row count = content max row (bottom edge of lowest widget); row height
  //                derived from canvas height. Empty grid falls back to reference_rows.
  // "fixed_rows" — row count = reference_rows (clamped 1..128); row height derived from
  //                canvas height.
  //
  // Collapsed borders, so the canvas splits into `rows` steps plus the one closing pixel of
  // the last cell's bottom border:
  //
  //     rows * rowStep + 1 === containerHeight        (exactly, in every mode)
  //
  // `rowStep` is the distance between two row boundaries — what the pitch target and the drop
  // arithmetic are about. `rowHeight` is the cell BOX react-grid-layout is given, one pixel
  // more, because a cell's box spans from its own top border through the shared bottom one.
  //
  // Both are kept fractional on purpose — rounding would reintroduce partial-row drift.
  // Returns { rows, rowStep, rowHeight }.
  effectiveGridGeometry(layout) {
    const H = Math.max(0, this.state.containerHeight || 600);
    const divisible = Math.max(1, H - 1); // the closing border pixel is not divisible

    const mode = this.resolveVerticalSizing(layout);
    let rows;
    if (mode === "fit") {
      const maxRow = this.contentMaxRow();
      rows = Math.max(1, maxRow || this.resolveReferenceRows(layout)); // empty grid → reference_rows
    } else if (mode === "fixed_rows") {
      rows = this.resolveReferenceRows(layout);
    } else { // "pitch" (default)
      const target = layout.row_height || 30;
      rows = Math.max(1, Math.round(divisible / target));
    }
    const rowStep = divisible / rows;
    return { rows, rowStep, rowHeight: rowStep + 1 };
  }

  // Distance between two column boundaries — the horizontal twin of rowStep, and the same
  // identity: `columns * columnStep + 1 === containerWidth`. Kept fractional for the same
  // reason; react-grid-layout rounds each item's own left/width off it, and rounding here
  // first would leave the last column short of the wall.
  columnStep(layout) {
    return Math.max(0, this.containerWidth() - 1) / Math.max(1, layout.columns || 1);
  }

  // Feeds the canvas gridline pattern: one background tile per step, each drawing its line at
  // the tile's leading edge. Lines therefore land on x = 0 (the left wall), on every column
  // boundary, and on x = columns * step === width - 1 (the right wall) — the same pixels a
  // widget sitting in those cells draws its own borders on. Same on the other axis.
  gridlineBackgroundStyle(layout, rowStep) {
    return {
      "--grid-step-x": `${this.columnStep(layout)}px`,
      "--grid-step-y": `${rowStep}px`,
    };
  }

  handleWidgetCellClick = (node) => (event) => {
    // Avoid hijacking interactions inside the widget body (inputs, buttons,
    // scroll, etc.). Selection happens when the click lands on the cell
    // wrapper or the drag handle.
    if (event.target.closest(".grid-widget-body")) return;
    this.props.shell.shellInterface.updateDetails("component", node);
  };

  handleCanvasClick = (event) => {
    // Click on empty grid space (not on any widget cell) clears selection.
    // Bubbling lets a widget cell click fire its own handler first; this
    // canvas handler then no-ops because the target is inside the cell.
    if (event.target.closest(".grid-widget-cell")) return;
    // Keep the widget selected (and its header visible) while focus is inside a widget or a widget-spawned
    // modal — typing in a widget's own controls must not clear the selection.
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (active && active.closest && (active.closest(".grid-widget-cell") || active.closest(".modal"))) {
      return;
    }
    const shellInterface = this.props.shell?.shellInterface;
    if (shellInterface?.updateDetails) {
      shellInterface.updateDetails(null, null);
    }
  };

  isCellSelected(node) {
    const details = this.props.shell.details;
    return details?.view === "component" && details?.data?.id === node.id;
  }

  renderWidgetCell(node, idx) {
    const Widget = WIDGET_BY_TYPE[node.type];
    if (!Widget) return null;
    const isSelected = this.isCellSelected(node);
    const selectedClass = isSelected ? " grid-widget-selected" : "";
    // Spread shell.zoom into data so the widget receives it as a prop and
    // re-renders when the zoom slider moves; the layout itself stays at 1x.
    const data = { ...node.data, zoom: this.props.shell.zoom ?? 1 };
    return (
      <div
        key={node.id}
        data-component-id={node.id}
        className={"grid-widget-cell" + selectedClass}
        // A cell's boundary pixels are shared with its neighbours, so the selected one has to
        // paint last or their border colour would win on the very edges the ring is made of.
        style={{ zIndex: isSelected ? SELECTED_CELL_Z : idx }}
        onClick={this.handleWidgetCellClick(node)}
      >
        <Widget data={data} />
      </div>
    );
  }

  // Whether focus currently sits inside the given widget's cell — the guard that keeps the Delete
  // shortcut from stealing the key from a nested editor. Mirrors the free-form canvas's
  // isFocusInsideSelectedComponent(); a widget-spawned modal portals to <body>, so it counts too.
  isFocusInsideComponent(componentId) {
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (!active || !active.closest) return false;
    if (active.closest(".modal")) return true;
    const escaped = (typeof CSS !== "undefined" && CSS.escape) ? CSS.escape(componentId) : componentId;
    return !!active.closest(`[data-component-id="${escaped}"]`);
  }

  render() {
    const layout = this.resolveLayout();
    if (!layout || layout.type !== "grid") return null;

    // Freeze geometry during an active drag/resize so "fit" mode does not
    // rescale the row height mid-gesture (which would shift widgets under
    // the cursor). On interaction end the flag clears, causing a re-render
    // that recomputes geometry from the committed layout positions.
    let geometry;
    if (this.state.interacting && this.lastGeometry) {
      geometry = this.lastGeometry;
    } else {
      geometry = this.effectiveGridGeometry(layout);
      this.lastGeometry = geometry;
    }

    const gridNodes = this.props.shell.nodes;
    const layoutItems = gridNodes.map((n) => n.layout);
    const isEmpty = gridNodes.length === 0;

    const containerContentClass
      = this.props.shell.wideContent || this.props.shell.library.visible || this.props.shell.details.visible
      ? "content-row-wide"
      : "content-row";

    // Stay mounted when the tab isn't shown so embedded widgets
    // (TradingView, etc.) keep their internal state across tab switches.
    // Free-form behaves the same way; only the early return that used to
    // guard this branch caused grid widgets to remount.
    const containerStyle = this.props.shown ? undefined : { display: "none" };

    return (
      <Container className={"blob-header-row " + containerContentClass} style={containerStyle}>
        <Row>
          <Col sm={3} className={`detail-panel-col ${this.props.shell.library.visible ? "" : "hidden"}`}>
            <LibraryPane shell={this.props.shell} onAddComponent={this.handleAddComponent} />
          </Col>

          <Col>
            <Card>
              <Card.Body>
                <div className="workflow-controls-pane-left playground-grid-controls">
                  {!this.props.shell.library.visible &&
                    <Button variant="light" onClick={() => {
                      this.props.shell.shellInterface.updatePane("library", true);
                    }}><GoPlus /></Button>
                  }
                </div>

                <div className="workflow-controls-pane-right playground-grid-controls">
                  {this.props.shell.modified &&
                    <>
                      <Tooltip text="Revert all changes">
                        <Button variant="light" onClick={() => {
                          this.props.shell.shellInterface.revertPlayground();
                        }}><GrRevert /></Button>
                      </Tooltip>
                      &nbsp;&nbsp;
                      <Tooltip text="Save changes">
                        <Button variant="light" onClick={() => {
                          this.props.shell.shellInterface.savePlayground();
                        }}><TfiSave /></Button>
                      </Tooltip>
                      &nbsp;&nbsp;
                    </>
                  }

                  <Tooltip text="Toggle width">
                    <Button variant="light" onClick={() => {
                      this.props.shell.shellInterface.toggleWideContent();
                    }}>
                      {this.props.shell.wideContent ? <BiCollapseHorizontal /> : <BiExpandHorizontal />}
                    </Button>
                  </Tooltip>

                  {!this.props.shell.details.visible &&
                    <>
                      &nbsp;&nbsp;
                      <Tooltip text="Show details">
                        <Button variant="light" onClick={() => {
                          this.props.shell.shellInterface.updatePane("details", true);
                        }}><GoInfo /></Button>
                      </Tooltip>
                    </>
                  }
                </div>

                <div
                  ref={this.containerRef}
                  className="grid-canvas"
                  onClick={this.handleCanvasClick}
                  onDragOver={this.handleCanvasDragOver}
                  onDrop={this.handleCanvasDrop}
                >
                <div
                  className="grid-canvas-inner"
                  style={this.gridlineBackgroundStyle(layout, geometry.rowStep)}
                >
                  <GridLayout
                    className="grid-layout"
                    width={this.containerWidth()}
                    layout={layoutItems}
                    gridConfig={{
                      cols: layout.columns,
                      rowHeight: geometry.rowHeight,
                      // Collapsed borders — see GRID_MARGIN. The negative margin is what makes
                      // neighbouring cells share their boundary pixel instead of each drawing
                      // one; the pattern behind them is painted on those same pixels.
                      margin: GRID_MARGIN,
                      containerPadding: GRID_CONTAINER_PADDING,
                    }}
                    dragConfig={{
                      enabled: this.canEdit(),
                      handle: ".card-header",
                      cancel: ".btn",
                    }}
                    resizeConfig={{
                      enabled: this.canEdit(),
                      handles: ["se"],
                    }}
                    dropConfig={{
                      enabled: this.canEdit(),
                      defaultItem: this.state.droppingItem,
                    }}
                    compactor={noCompactor}
                    constraints={[]}
                    droppingItem={this.state.droppingItem}
                    onLayoutChange={this.handleLayoutChange}
                    onDrop={this.handleDrop}
                    onDragStart={this.handleInteractionStart}
                    onDragStop={this.handleInteractionEnd}
                    onResizeStart={this.handleInteractionStart}
                    onResizeStop={this.handleInteractionEnd}
                  >
                    {gridNodes.map((node, idx) => this.renderWidgetCell(node, idx))}
                  </GridLayout>
                  {isEmpty && (
                    <div className="grid-empty-hint">
                      Drag a widget from the sidebar to start
                    </div>
                  )}
                </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm={3} className={`detail-panel-col ${this.props.shell.details.visible ? "" : "hidden"}`}>
            <DetailsPane shell={this.props.shell} timezone={this.props.timezone} />
          </Col>
        </Row>
      </Container>
    );
  }
}
